import {
  TermsAndCondition,
  faqs,
  howItWorks,
  purchaseGiftCardLanguages,
  purchaseSeatsLanguages,
  redeemGiftCardLanguages,
} from "./helpers";
import { routes } from "@/global/routes";
import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";
import BackgroundImage from "public/images/diamondbacks/dbacks_bg.jpeg";
import { vendorUUIDs } from "../../constants";
import BannerImage from "public/images/diamondbacks/dbacks_suites_banner_03_24.jpg";
import { TeamConfig } from "@/global/hooks/useTeamConfig";
import {
  DbacksAdditionalDetailsDisclaimerTC,
  DbacksHamburgerTCListItems,
  DbacksTCLanguage,
} from "../../tc_helper_dbacks";
import { createJsxLink } from "@/global/utils/utils.dom";

const LogoImage = "https://ss-leagues.s3.amazonaws.com/MLB-3/AZD-UPDATED.png";

const config: TeamConfig = {
  teamSchema: "flex_sandbox_suite",
  browserTitle: "Arizona Diamondbacks",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Flex Sandbox",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10167-diamondbacks-sec",
    mapId: "blockmap2d",

    isShowAvailableSeatsButtonHidden: true,

    suiteImages: [
      "https://ss-leagues.s3.amazonaws.com/AZDBacks%20Assets/AZD%20Suites%20Images/Image%201.jpg",
      "https://ss-leagues.s3.amazonaws.com/AZDBacks%20Assets/AZD%20Suites%20Images/Image%202.jpg",
      "https://ss-leagues.s3.amazonaws.com/AZDBacks%20Assets/AZD%20Suites%20Images/Image%203.jpg",
      "https://ss-leagues.s3.amazonaws.com/AZDBacks%20Assets/AZD%20Suites%20Images/Image%204.jpg",
    ],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: "SUITE-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 0,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    invPreviewAccountGroup: ["0"],
    isPerksEnabled: true,
    isCheckoutAdditionalDetailsEnabled: true,
    hideViewTicketsInThankYouPage: true,
  },

  oktaClientId: "",
  oktaIssuer: "",
  platform: vendorUUIDs.ticketmaster,
  refreshInventoryWhenEventClick: false,

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  isSaveCardAllowed: true,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/azdbacks/my-events",

  // images
  images: {
    findGamesBannerImage: BannerImage.src,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    giftCardStandalonePageImage: BannerImage.src,
    logoImage: LogoImage + "?width=84&height=66",
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: false,
    },

    inventoryContainer: {
      emptyStateTitle: "No D-backs Suites Available.",
      emptyStateDesc: (
        <>
          Try another game or email{" "}
          {createJsxLink("suites@dbacks.com", "mailto:suites@dbacks.com")} for
          suite availability to this game
        </>
      ),
      getGridCellHeight: (arg) => (arg?.mdDown ? 240 : 175),
    },

    inventoryCardSettings: {
      isPBLineItemsVisible: true,
      seatCountLabel: "Tickets Included",
    },

    perks: {
      hideAddAllButton: true,
    },
  },

  colors: {
    primary: "#AA182C",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: <DbacksHamburgerTCListItems />, // can be react node as well
    checkoutTermsAndCondition: <DbacksTCLanguage />,
    registrationTermsAndCondition: <DbacksTCLanguage />,
    additionalDetailsDisclaimerTC: <DbacksAdditionalDetailsDisclaimerTC />,
    purchaseSeats: purchaseSeatsLanguages,
    purchaseGiftCard: purchaseGiftCardLanguages,
    redeemGiftCard: redeemGiftCardLanguages,
    checkoutServiceNote:
      "Your reservation is not yet complete until notification from the Arizona Diamondbacks confirming your reservation has been accepted in accordance with our agreement.",

    giftCard: {
      title: "Give The Gift Of Broadway",
      createGiftCard: {
        description:
          "Share the magic of Broadway with a friend or loved one. Upon purchase of a gift card, a unique code will be generated for the intended recipient to redeem. Once redeemed, the gift card is not refundable.",
        recipient: {
          sendNowFalse: {
            description:
              "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift card, they will be prompted to enter their unique code to redeem the gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Hippodrome Broadway Series shows in Baltimore.",
          },
          sendNowTrue: {
            description:
              "The Recipient will be sent an email with the unique code for them to redeem their gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Hippodrome Broadway Series shows in Baltimore.",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Hippodrome Broadway Series in Baltimore account above.",
        },
        logout: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Hippodrome Broadway Series in Baltimore.",
        },
        success: {
          description:
            "has been added to your Hippodrome Broadway Series in Baltimore Account Manager account!",
        },
      },
    },
    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
